import axios from 'axios';
import { baseUrl } from '../hooks/useEndpoints';
import {
  ICatalogResponse,
  ICreateSessionInput,
  ICustomGroup,
  ISession,
  ISavedView,
  ITableResponse,
  IUserColumnPreset,
  TableMode,
  UnitMode,
} from '../models/sessions';

export interface ITableQuery {
  periodStart: string;
  periodEnd: string;
  mode?: TableMode;
  presetId?: string;
  columns?: string[];
  unitMode?: UnitMode;
  roles?: string[];
  sessionTypes?: string[];
  customGroupId?: string;
  kpiMetric?: string;
  /** Bucket intensità: low / medium / high */
  intensityBuckets?: string[];
  /** Range RPE min/max (serializzato come "min,max") */
  rpeRange?: string;
  /** Filtro percentile: lt_p25 / p25_p75 / gt_p75 / outlier */
  percentileFilter?: string;
  /** Filtro baseline: above / in_line / below (CSV) */
  baselineFilter?: string;
  /** Filtro categoria attività (CSV) */
  activityCategoryFilter?: string;
}

export const sessionsEndpoints = {
  catalog: () => axios.get<ICatalogResponse>(`${baseUrl}/sessions/catalog`),

  getTeamTable: (teamId: string, params: ITableQuery) =>
    axios.get<ITableResponse>(`${baseUrl}/sessions/team/${teamId}/table`, {
      params: {
        ...params,
        columns: params.columns?.join(','),
        roles: params.roles?.join(','),
        sessionTypes: params.sessionTypes?.join(','),
        intensityBuckets: params.intensityBuckets?.join(','),
        baselineFilter: params.baselineFilter,
        activityCategoryFilter: params.activityCategoryFilter,
        // rpeRange e percentileFilter sono già stringhe, passati as-is
      },
    }),

  listForAthlete: (athleteId: string, limit?: number) =>
    axios.get<ISession[]>(`${baseUrl}/sessions/athlete/${athleteId}`, {
      params: { limit },
    }),

  getOne: (id: string) => axios.get<ISession>(`${baseUrl}/sessions/${id}`),

  create: (body: ICreateSessionInput) =>
    axios.post<ISession>(`${baseUrl}/sessions`, body),

  remove: (id: string) =>
    axios.delete<{ ok: true }>(`${baseUrl}/sessions/${id}`),

  applyQualityOverride: (
    sessionId: string,
    body: { athleteId: string; reason: string; category: string },
  ) =>
    axios.post(`${baseUrl}/sessions/${sessionId}/quality-override`, body),

  revokeOverride: (overrideId: string) =>
    axios.delete(`${baseUrl}/sessions/quality-override/${overrideId}`),
};

export const columnPresetsEndpoints = {
  list: (teamId?: string) =>
    axios.get<IUserColumnPreset[]>(`${baseUrl}/column-presets`, {
      params: { teamId },
    }),
  create: (body: {
    name: string;
    columns: string[];
    scope?: 'user' | 'team';
    teamId?: string;
    isDefault?: boolean;
  }) => axios.post<IUserColumnPreset>(`${baseUrl}/column-presets`, body),
  update: (id: string, body: Partial<{ name: string; columns: string[] }>) =>
    axios.patch<IUserColumnPreset>(`${baseUrl}/column-presets/${id}`, body),
  remove: (id: string) => axios.delete(`${baseUrl}/column-presets/${id}`),
};

export const groupsEndpoints = {
  list: (teamId?: string) =>
    axios.get<ICustomGroup[]>(`${baseUrl}/groups`, { params: { teamId } }),
  create: (body: {
    name: string;
    athleteIds: string[];
    description?: string;
    color?: string;
    scope?: 'user' | 'team';
    teamId?: string;
  }) => axios.post<ICustomGroup>(`${baseUrl}/groups`, body),
  update: (id: string, body: Partial<ICustomGroup>) =>
    axios.patch<ICustomGroup>(`${baseUrl}/groups/${id}`, body),
  remove: (id: string) => axios.delete(`${baseUrl}/groups/${id}`),
};

// ─────────────────────────────────────────────────────────────────
// CSV Wizard endpoints (Addendum v5)
// ─────────────────────────────────────────────────────────────────

export interface ICsvStructure {
  encoding: string;
  separator: ',' | ';' | '\t' | '|';
  quoteChar: '"' | "'" | '';
  decimalSeparator: '.' | ',';
  thousandSeparator: '.' | ',' | ' ' | '';
  headerRow: number;
  rowCount: number;
  columnCount: number;
}

export interface ICsvMappingEntryFE {
  csvColumnIndex: number;
  csvColumnName: string;
  leopartMetricId: string;
  unit?: string;
  ignored?: boolean;
}

export interface ICsvAnalyzeResponse {
  filename: string;
  structure: ICsvStructure;
  columns: string[];
  sampleRows: Record<string, string>[];
  totalDataRows: number;
  templateMatch?: {
    templateId: string;
    templateName: string;
    mapping: ICsvMappingEntryFE[];
  };
  suggestions: Record<string, string>;
  // M6.2 auto-detect fields
  detectedDataType?: 'gps_full' | 'cardio_only' | 'running_cycling' | 'manual';
  detectConfidence?: number;
  detectSignals?: string[];
  needsManualChoice?: boolean;
  detectedTeamFromFile?: string | null;
}

export interface ICsvAthleteMatch {
  csvValue: string;
  occurrences: number;
  matchedAthleteId?: string;
  matchedAthleteName?: string;
  matchType: 'exact' | 'alias' | 'fuzzy' | 'none';
  confidence: number;
}

export interface ICsvPreviewRow {
  rowIndex: number;
  athleteId?: string;
  athleteName?: string;
  date?: string;
  sessionDraft: any;
  warnings: string[];
  conflict?: { existingSessionId: string; note: string };
}

export interface ICsvPreviewResponse {
  rows: ICsvPreviewRow[];
  totals: {
    totalRows: number;
    importable: number;
    skippedNoAthlete: number;
    skippedNoDate: number;
    conflicts: number;
  };
}

export interface ICsvExecuteResponse {
  status: 'completed' | 'failed';
  rowsImported: number;
  rowsUpdated: number;
  rowsSkipped: number;
  templateSaved?: { templateId: string; templateName: string };
  errors: string[];
  durationMs: number;
}

export interface ICsvMappingTemplate {
  _id: string;
  name: string;
  headerFingerprint: string;
  headerColumns: string[];
  mapping: ICsvMappingEntryFE[];
  scope: 'user' | 'team';
  ownerId: string;
  teamId?: string;
  usageCount?: number;
  lastUsedAt?: string;
  createdAt?: string;
}

export const csvWizardEndpoints = {
  analyze: (body: {
    fileBase64: string;
    filename: string;
    teamId?: string;
    encoding?: string;
    separator?: ',' | ';' | '\t' | '|';
    decimalSeparator?: '.' | ',';
  }) =>
    axios.post<ICsvAnalyzeResponse>(`${baseUrl}/csv-imports/analyze`, body),

  matchAthletes: (body: {
    fileBase64: string;
    encoding: string;
    separator: ',' | ';' | '\t' | '|';
    quoteChar: '"' | "'" | '';
    decimalSeparator: '.' | ',';
    thousandSeparator: '.' | ',' | ' ' | '';
    headerRow: number;
    athleteColumnIndex: number;
    teamId: string;
  }) =>
    axios.post<{
      uniqueValues: string[];
      matches: ICsvAthleteMatch[];
    }>(`${baseUrl}/csv-imports/match-athletes`, body),

  preview: (body: any) =>
    axios.post<ICsvPreviewResponse>(`${baseUrl}/csv-imports/preview`, body),

  execute: (body: any) =>
    axios.post<ICsvExecuteResponse>(`${baseUrl}/csv-imports/execute`, body),

  listTemplates: (teamId?: string) =>
    axios.get<ICsvMappingTemplate[]>(`${baseUrl}/csv-imports/templates`, {
      params: { teamId },
    }),

  removeTemplate: (id: string) =>
    axios.delete(`${baseUrl}/csv-imports/templates/${id}`),
};

export const savedViewsEndpoints = {
  list: (teamId?: string) =>
    axios.get<ISavedView[]>(`${baseUrl}/saved-views`, { params: { teamId } }),
  create: (body: Partial<ISavedView>) =>
    axios.post<ISavedView>(`${baseUrl}/saved-views`, body),
  update: (id: string, body: Partial<ISavedView>) =>
    axios.patch<ISavedView>(`${baseUrl}/saved-views/${id}`, body),
  remove: (id: string) => axios.delete(`${baseUrl}/saved-views/${id}`),
};

export const gpsImportEndpoints = {
  importFile: (body: {
    filename: string;
    fileBase64: string;
    athleteId: string;
    teamId?: string;
    sportOverride?: string;
    linkedAssignmentId?: string;
  }) =>
    axios.post<{
      sessionId: string;
      created: boolean;
      qualityClass?: string;
      duration?: number;
      td?: number;
    }>(`${baseUrl}/sessions/import`, body),
};
